import * as React from "react";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { GradientButton } from "@/components/gradient-button";
import { CALENDLY_30MIN_URL } from "@/lib/cta";
import { capture } from "@/lib/analytics";
import { attachCalendlyTracking } from "@/lib/analytics/calendly-tracking";

type Props = React.ComponentProps<typeof GradientButton>;

/**
 * Calendly only emits its `calendly.*` postMessage events — the ones
 * `attachCalendlyTracking()` listens for — when the embed declares the parent
 * it should message. Without `embed_domain`/`embed_type`, a raw iframe books
 * demos silently and every conversion event reads zero.
 */
function calendlyEmbedSrc(): string {
  const params = new URLSearchParams({
    hide_gdpr_banner: "1",
    embed_domain: window.location.hostname,
    embed_type: "Inline",
  });
  return `${CALENDLY_30MIN_URL}?${params}`;
}

/**
 * The Calendly booking modal.
 *
 * Every "book a demo" / "talk to us" CTA must route through this rather than
 * linking to a Calendly URL directly. `attachCalendlyTracking()` reads
 * Calendly's `postMessage` events off the embedded iframe, so a booking made on
 * calendly.com's own origin is invisible here and `demo_booked` never fires.
 *
 * Pass `children` to render the trigger inline. When the trigger lives inside
 * another component (`LandingHeader`), or several CTAs share one modal, drive
 * `open`/`onOpenChange` from the caller and omit `children`.
 */
export function BookDemoDialog({
  open: controlledOpen,
  onOpenChange,
  children,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  useEffect(() => {
    if (!open) return;
    capture("demo_cta_opened", { page_path: window.location.pathname });
    return attachCalendlyTracking();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children ? <DialogTrigger asChild>{children}</DialogTrigger> : null}
      {/*
       * Gated on `open` rather than left to Radix's own unmounting: a JSX prop
       * is evaluated when the element is *created*, so an inline
       * `src={calendlyEmbedSrc()}` runs on every server render — `window` is
       * undefined there — even though `DialogContent` itself never mounts.
       * Callers must therefore not pass `open` as `true` on the first render.
       */}
      {open && (
        <DialogContent className="max-w-3xl overflow-hidden border-white/10 bg-background p-0 [&>button]:text-black">
          <DialogTitle className="sr-only">Book a demo with Axy.digital</DialogTitle>
          <iframe
            src={calendlyEmbedSrc()}
            title="Book a demo with Axy"
            className="h-[78vh] w-full border-0"
          />
        </DialogContent>
      )}
    </Dialog>
  );
}

/** A gradient CTA button that opens the Calendly booking modal. */
export function BookDemoButton({ children = "Book a demo", ...props }: Props) {
  return (
    <BookDemoDialog>
      <GradientButton {...props}>{children}</GradientButton>
    </BookDemoDialog>
  );
}
